import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

import nivelesImg from '../../assets/images/niveleshawkins.png';
import nivelesPdf from '../../assets/pdfs/niveles_hawkins.pdf';
import portadaImg from '../../assets/images/portada.png';
import UrlConfig from '../../data/UrlConfig';

// --- Componente de Tarjeta de Contenido ---
const ContentCard = ({ children, className = '' }) => (
  <div className={`bg-gray-800/50 p-8 rounded-lg border border-gray-700 ${className}`}>
    {children}
  </div>
);

// --- Icono para el botón de descarga ---
const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const NivelesHawkinsPage = () => {
  const config = UrlConfig;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [nick, setNick] = useState('');
  const [user, setUser] = useState({});
  const [errors, setErrors] = useState({ email: '', nick: '' });
  const [mensajeExito, setMensajeExito] = useState('¡Tu PDF está en camino! Revisa tu carpeta de descargas.');

  useEffect(() => {
    // Estilo inmersivo para la página
    document.body.className = 'bg-black text-white';
    document.title = 'Niveles de Conciencia | Gustavo Aguiar';

    let data = localStorage.getItem('userSM');
    if (data) {
      let userSM = JSON.parse(data);
      userSM.nivelesDownloaded = userSM.statuses?.some(item => item.page === 'niveles' && item.status === 'downloaded');
      setEmail(userSM.email);
      setNick(userSM.nick);
      setUser(userSM);
    }

    // Limpieza al salir del componente
    return () => {
      document.body.className = ''; // Restaura las clases del body
      document.title = 'GUS • A • GUIAR';
    };
  }, []);

  const validar = () => {
    const nuevosErrores = { email: '', nick: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) nuevosErrores.email = 'Ingresa un email válido.';
    if (!nick.trim()) nuevosErrores.nick = 'Tu nombre es obligatorio.';
    setErrors(nuevosErrores);
    return !nuevosErrores.email && !nuevosErrores.nick;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;

    const datos = { email, nick, page: 'niveles', section: 'pdf', status: 'downloaded' };

    try {
      const response = await fetch(config.resourcesApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(datos),
      });

      const resultado = await response.json();

      if (resultado.status !== 'success') {
        throw new Error('Error en el servidor: ' + resultado.message);
      }

      const newStatus = { page: 'niveles', section: 'pdf', status: 'downloaded' };
      const updatedUser = {
        ...user,
        email,
        nick,
        statuses: [...(user.statuses || []), newStatus],
        nivelesDownloaded: true,
      };

      localStorage.setItem('userSM', JSON.stringify(updatedUser));
      setUser(updatedUser);

      // Iniciar la descarga
      const link = document.createElement('a');
      link.href = nivelesPdf;
      link.download = 'Niveles de Conciencia - Hawkins.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error en la descarga:', error);
      alert('Ocurrió un error al intentar descargar el archivo. Por favor, intenta de nuevo.');
    }
  };

  const [heroRef, heroInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [mainRef, mainInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [formRef, formInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [ctaRef, ctaInView] = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <div className="bg-black text-white font-sans selection:bg-purple-500 selection:text-black">
      {/* --- Header --- */}
      <header className="absolute top-0 left-0 w-full p-6 z-30 flex justify-between items-center">
        <Link to="/" className="text-lg tracking-widest text-gray-400 hover:text-white transition-colors duration-300">GUS•A•GUIAR</Link>
        <Link to="/menu" className="text-sm font-semibold text-gray-400 hover:text-white transition-colors duration-300">MENÚ</Link>
      </header>

      {/* --- Hero Section --- */}
      <section ref={heroRef} className={`min-h-screen flex flex-col items-center justify-center text-center p-4 pt-24 sm:pt-16 bg-gray-900 ${heroInView ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-400">
            Niveles de Conciencia: El Origen de tu Realidad
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
            Descubre el mapa que revela cómo tus emociones y pensamientos internos crean tu mundo externo.
          </p>
        </div>
        <img src={nivelesImg} alt="Mapa de los Niveles de Conciencia" className="mt-12 w-full max-w-2xl rounded-lg shadow-2xl shadow-purple-500/20 border-2 border-purple-500/20" />
      </section>

      {/* --- Main Content Section --- */}
      <section ref={mainRef} className={`py-20 sm:py-32 bg-black ${mainInView ? 'animate-slide-in-left' : 'opacity-0 transform -translate-x-10'}`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-center">
          <div className="prose prose-invert prose-lg text-gray-300">
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-400 mb-6">Del Ser, no del Hacer</h2>
            <p>Los <strong>niveles de conciencia</strong> son las bases y el contexto que todo lo envuelve. No se trata de lo que haces o tienes, sino de quién <strong>eres</strong>.</p>
            <p>Tu nivel de conciencia es la frecuencia energética que emites, y esa frecuencia atrae circunstancias, personas y realidades que vibran en sintonía. Entender esto es el primer paso para cambiar tu vida desde la raíz.</p>
          </div>
          <div className="relative pt-[56.25%] bg-black rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20">
            <iframe
              src="https://www.youtube.com/embed/N6vpI1-2cd4?autoplay=0&rel=0&controls=1&showinfo=0&modestbranding=1"
              title="Explicación de los Niveles de Conciencia"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full"
            ></iframe>
          </div>
        </div>
      </section>

      {/* --- Download Form Section --- */}
      <section ref={formRef} className={`py-20 sm:py-32 bg-gray-900 ${formInView ? 'animate-slide-in-right' : 'opacity-0 transform translate-x-10'}`}>
        <div className="max-w-xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Descarga el Mapa de la Conciencia</h2>
          <p className="text-lg text-gray-400 mb-12">Obtén el PDF con la escala completa y ejercicios prácticos para identificar tu nivel actual y empezar a elevarlo.</p>
          {user.nivelesDownloaded ? (
            <ContentCard className="text-center">
              <p className="text-2xl font-bold text-green-400">¡Éxito!</p>
              <p className="mt-2 text-gray-300">{mensajeExito}</p>
              <button onClick={() => navigate(0)} className="mt-6 bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105">Descargar de Nuevo</button>
            </ContentCard>
          ) : (
            <ContentCard>
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="nick" className="block text-sm font-medium text-gray-300 mb-2">Nombre</label>
                    <input id="nick" type="text" value={nick} onChange={(e) => setNick(e.target.value)} className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition" placeholder="Tu nombre" />
                    {errors.nick && <p className="mt-2 text-sm text-red-400">{errors.nick}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition" placeholder="tu@email.com" />
                    {errors.email && <p className="mt-2 text-sm text-red-400">{errors.email}</p>}
                  </div>
                </div>
                <button type="submit" className="mt-8 w-full flex items-center justify-center bg-purple-500 hover:bg-purple-600 text-white font-bold text-lg py-4 px-8 rounded-full shadow-lg shadow-purple-500/30 transform hover:scale-105 transition-all duration-300">
                  <DownloadIcon />
                  Descargar PDF Gratis
                </button>
              </form>
            </ContentCard>
          )}
        </div>
      </section>

      {/* --- "What's Next?" CTA Section --- */}
      {user.nivelesDownloaded && (
        <section ref={ctaRef} className={`py-20 sm:py-32 bg-black ${ctaInView ? 'animate-slide-in-bottom' : 'opacity-0 transform translate-y-10'}`}>
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-6">¿Qué sigue?</h2>
            <p className="text-lg text-gray-300 mb-4">La forma más sencilla de elevar tu nivel de conciencia es a través de los pensamientos. Puedes empezar ahora mismo.</p>
            <p className="text-lg text-gray-300 mb-8">Cuando termines el ejercicio del PDF, te invito a que indaguemos juntos en el poder de las afirmaciones.</p>
            <Link
              to="/afirmaciones"
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold text-xl py-5 px-12 rounded-full shadow-lg shadow-purple-500/40 transform hover:scale-105 transition-all duration-300 inline-block"
            >
              IR A AFIRMACIONES DE PODER
            </Link>
          </div>
        </section>
      )}

      {/* --- Footer --- */}
      <footer className="text-center py-8 border-t border-gray-800 bg-black">
        <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Gus • Aguiar | En misión de elevar y transformar.</p>
        <div className="mt-4">
          <Link to="/menu" className="text-sm text-gray-400 hover:text-purple-300 transition-colors">
            ¿No encontraste lo que buscabas?
          </Link>
        </div>
      </footer>
    </div>
  );
}

export default NivelesHawkinsPage;